// StudentAdmin.js - Admin panel for browsing students and viewing their analytics
// 3 views: sections list -> students list -> student detail (Badge + Heatmap tabs)

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, apiFetch } from '../config';
import BadgePanel from './BadgePanel';
import StruggleHeatmap from './StruggleHeatmap';
import AnalyticsDashboard from '../AnalyticsDashboard';
import ClassQuizResults from './ClassQuizResults';

const StudentAdmin = () => {
  const [view, setView] = useState('sections'); // 'sections' | 'students' | 'detail'
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [activeTab, setActiveTab] = useState(null); // null | 'badge' | 'heatmap'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const adminUsername = localStorage.getItem('username') || '';

  // ===== Fetch sections =====
  const fetchSections = useCallback(async () => {
    if (!adminUsername) {
      setError('No admin username found in session.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/api/admin/students/sections?username=${encodeURIComponent(adminUsername)}`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSections(data || []);
    } catch (e) {
      console.error('Error fetching sections:', e);
      setError('Failed to load sections.');
    } finally {
      setLoading(false);
    }
  }, [adminUsername]);

  useEffect(() => {
    if (view === 'sections') fetchSections();
  }, [view, fetchSections]);

  // ===== Fetch students for selected section =====
  const fetchStudents = useCallback(async (section) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        classSection: section.classSection,
        university: section.university,
        semester: section.semester,
      });
      const res = await apiFetch(`${API_BASE_URL}/api/admin/students?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStudents(data || []);
    } catch (e) {
      console.error('Error fetching students:', e);
      setError('Failed to load students.');
    } finally {
      setLoading(false);
    }
  }, []);

  // ===== Navigation handlers =====
  const openSection = (section) => {
    setSelectedSection(section);
    setView('students');
    fetchStudents(section);
  };

  const openStudent = (student) => {
    setSelectedStudent(student);
    setActiveTab(null);
    setView('detail');
  };

  const backToSections = () => {
    setSelectedSection(null);
    setStudents([]);
    setView('sections');
  };

  const backToStudents = () => {
    setSelectedStudent(null);
    setView('students');
  };

  // ===== Styles =====
  const containerStyle = {
    padding: '24px',
    color: '#c9d1d9',
    height: '100%',
    overflowY: 'auto',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    borderBottom: '1px solid #30363d',
    paddingBottom: '12px',
  };

  const backBtnStyle = {
    backgroundColor: '#30363d',
    color: '#c9d1d9',
    border: '1px solid rgba(253, 230, 138, 0.35)',
    padding: '8px 14px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  };

  const cardStyle = {
    backgroundColor: '#1c2128',
    border: '1px solid rgba(253, 230, 138, 0.3)',
    borderRadius: '12px',
    padding: '28px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '200px',
  };

  // ===== View 1: sections =====
  const renderSections = () => (
    <div>
      <div style={headerStyle}>
        <h2 style={{ margin: 0, color: '#fff' }}>👥 My Class Sections</h2>
        <span style={{ color: '#8b949e', fontSize: '13px' }}>
          Admin: {adminUsername}
        </span>
      </div>

      {loading && <p>Loading sections...</p>}
      {error && <p style={{ color: '#f85149' }}>{error}</p>}
      {!loading && !error && sections.length === 0 && (
        <p style={{ color: '#8b949e' }}>
          No sections found for your university and semester.
        </p>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: '24px',
        }}
      >
        {sections.map((s, idx) => (
          <div
            key={idx}
            style={cardStyle}
            onClick={() => openSection(s)}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#58a6ff';
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(88,166,255,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#30363d';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🎓</div>
            <div style={{ fontSize: '26px', fontWeight: '700', color: '#58a6ff', marginBottom: '12px' }}>
              {s.classSection}
            </div>
            <div style={{ color: '#c9d1d9', fontSize: '16px', marginBottom: '8px' }}>
              🏫 {s.university}
            </div>
            <div style={{ color: '#c9d1d9', fontSize: '16px', marginBottom: '20px' }}>
              📅 {s.semester}
            </div>
            <div style={{
              marginTop: 'auto',
              backgroundColor: '#1a7f3c',
              color: '#fff',
              padding: '10px 0',
              borderRadius: '6px',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: '600',
            }}>
              View Students →
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ===== View 2: students =====
  const renderStudents = () => (
    <div>
      <div style={headerStyle}>
        <div>
          <button style={backBtnStyle} onClick={backToSections}>
            ← Back to Sections
          </button>
          <h2 style={{ margin: '12px 0 0 0', color: '#fff' }}>
            👨‍🎓 Students in {selectedSection?.classSection}
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#8b949e', fontSize: '13px' }}>
            {selectedSection?.university} · {selectedSection?.semester}
          </p>
        </div>
        <span style={{ color: '#8b949e', fontSize: '13px' }}>
          {students.length} student{students.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading && <p>Loading students...</p>}
      {error && <p style={{ color: '#f85149' }}>{error}</p>}
      {!loading && !error && students.length === 0 && (
        <p style={{ color: '#8b949e' }}>No students enrolled in this section.</p>
      )}

      {students.length > 0 && (
        <div
          style={{
            backgroundColor: '#1c2128',
            border: '1px solid #30363d',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#161b22' }}>
              <tr>
                <th style={thStyle}>Last Name</th>
                <th style={thStyle}>First Name</th>
                <th style={thStyle}>Username</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map((st, idx) => (
                <tr
                  key={st.id || idx}
                  style={{ borderTop: '1px solid #30363d' }}
                >
                  <td style={{ ...tdStyle, fontSize: '20px', color: '#fef9c3', fontWeight: '600' }}>{st.lastName || '-'}</td>
                  <td style={{ ...tdStyle, fontSize: '20px', color: '#fef9c3', fontWeight: '600' }}>{st.firstName || '-'}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '20px', color: '#fef9c3', fontWeight: '600' }}>
                    {st.username}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      style={{
                        backgroundColor: '#1a7f3c',
                        color: '#fff',
                        border: 'none',
                        padding: '12px 22px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '600',
                      }}
                      onClick={() => openStudent(st)}
                    >
                      📊 View Analytics
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ===== View 3: student detail (tabs launch modals) =====
  const renderDetail = () => {
    const tabBtn = (id, label, icon) => (
      <button
        onClick={() => setActiveTab(id)}
        style={{
          padding: '32px 40px',
          backgroundColor: activeTab === id ? '#2d2a1a' : '#1c2128',
          color: activeTab === id ? '#fde68a' : '#c9d1d9',
          border: '2px solid',
          borderColor: activeTab === id ? '#fde68a' : 'rgba(253, 230, 138, 0.35)',
          borderRadius: '14px',
          cursor: 'pointer',
          fontSize: '20px',
          fontWeight: '700',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '14px',
          minWidth: '220px',
          transition: 'all 0.15s ease',
          boxShadow: activeTab === id ? '0 0 16px rgba(253,230,138,0.2)' : 'none',
        }}
        onMouseEnter={(e) => {
          if (activeTab !== id) {
            e.currentTarget.style.borderColor = 'rgba(253,230,138,0.7)';
            e.currentTarget.style.backgroundColor = '#21262d';
          }
        }}
        onMouseLeave={(e) => {
          if (activeTab !== id) {
            e.currentTarget.style.borderColor = 'rgba(253,230,138,0.35)';
            e.currentTarget.style.backgroundColor = '#1c2128';
          }
        }}
      >
        <span style={{ fontSize: '40px' }}>{icon}</span>
        <span>{label}</span>
      </button>
    );

    return (
      <div>
        <div style={headerStyle}>
          <div>
            <button style={backBtnStyle} onClick={backToStudents}>
              ← Back to Students
            </button>
            <h2 style={{ margin: '12px 0 0 0', color: '#fde68a', fontSize: '36px', fontWeight: '700' }}>
              {selectedStudent?.firstName} {selectedStudent?.lastName}
            </h2>
            <p style={{ margin: '4px 0 0 0', color: '#f0f6fc', fontSize: '18px', fontFamily: 'monospace' }}>
              {selectedStudent?.username}
            </p>
          </div>
        </div>

        <p style={{ color: '#f0f6fc', fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
          Select a panel to view analytics for this student:
        </p>

        {/* Tabs — each opens the corresponding full-screen modal */}
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '12px' }}>
          {tabBtn('badge', 'Badge & Points', '🏆')}
          {tabBtn('heatmap', 'Learning Progress / Struggles', '📊')}
          {tabBtn('activity', 'My Activity', '📈')}
          {tabBtn('quiz', 'Class Quiz Results', '📝')}
        </div>
      </div>
    );
  };

  // ===== Render current view =====
  return (
    <div style={containerStyle}>
      {view === 'sections' && renderSections()}
      {view === 'students' && renderStudents()}
      {view === 'detail' && selectedStudent && renderDetail()}

      {/* Modals rendered at top level so position:fixed works correctly */}
      {view === 'detail' && selectedStudent && activeTab === 'badge' && (
        <BadgePanel
          username={selectedStudent.username}
          onClose={() => setActiveTab(null)}
        />
      )}
      {view === 'detail' && selectedStudent && activeTab === 'heatmap' && (
        <StruggleHeatmap
          username={selectedStudent.username}
          onClose={() => setActiveTab(null)}
          onPracticeRecommendation={() => {}}
        />
      )}
      {view === 'detail' && selectedStudent && activeTab === 'activity' && (
        <AnalyticsDashboard
          username={selectedStudent.username}
          onClose={() => setActiveTab(null)}
        />
      )}
      {view === 'detail' && selectedStudent && activeTab === 'quiz' && (
        <ClassQuizResults
          username={selectedStudent.username}
          onClose={() => setActiveTab(null)}
          selectedSection={selectedSection?.classSection}
        />
      )}
    </div>
  );
};

const thStyle = {
  padding: '20px 24px',
  textAlign: 'left',
  color: '#c9d1d9',
  fontSize: '16px',
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const tdStyle = {
  padding: '20px 24px',
  textAlign: 'left',
  color: '#c9d1d9',
  fontSize: '16px',
};

export default StudentAdmin;
